/*
 * Active's rail overlays — PROTOTYPE SCAFFOLDING, not a feature.
 *
 * WHAT IS LEFT OF ROADMAP ITEM 4'S TOGGLE. It began as three whole candidate
 * layouts behind `?layout=a|b|c`. Kevin has adopted `c` — the flush card, the
 * halved padding and the hanging headings — as Active's standard, so those
 * rules are no longer a variant at all: they live in Active.module.css as the
 * screen's own declarations, which is what every visit renders.
 *
 * What survives is the one question the adoption did not settle: whether
 * anything should run down the inline-start edge of a card of fencers. Two
 * candidates remain, and both are pure ADDITIONS to the standard layout:
 *
 *   a   a thin neutral rule, drawn inside the padding the rows already have
 *   b   the event header's own tint, doubled and flush to the card edge
 *
 * So the toggle no longer swaps layouts; it adds a mark to one. That is why
 * the default is `none` rather than a baseline variant — there is no longer an
 * "old screen" to compare against, and keeping one would invite arguing from a
 * rendering already agreed to be wrong.
 *
 * THE URL PARAMETER IS STILL `layout`, which is now a slight misnomer. Kept
 * deliberately: Kevin and whoever he is collecting opinions from have the
 * links already, and renaming it mid-review would break them to fix a word.
 *
 * WHEN THE QUESTION IS SETTLED this file goes, with the two `rail*` classes in
 * Grouped.module.css and the prop threading in Active.tsx and Routes.tsx. If a
 * rail wins, its rule moves into the standard alongside everything else `c`
 * brought; if neither does, all of it is deleted.
 */

/** Which rail, if any, to draw beside a card of fencers. */
export type ActiveRail =
  /** The standard: no rail. What every ordinary visit renders. */
  | "none"
  /** A thin neutral rule, inset into the rows' existing padding. */
  | "a"
  /** The header's tint, doubled and flush to the card's inline edge. */
  | "b";

const RAILS: readonly ActiveRail[] = ["none", "a", "b"];

/**
 * Reads the rail from a query string.
 *
 * TAKES THE SEARCH STRING RATHER THAN READING `location`, so it is testable and
 * so the caller decides where the URL comes from — which on a router-driven
 * screen is the router, not the global. An unrecognised value falls back to
 * `none` rather than throwing: this is a debugging affordance reached by
 * hand-typing, and the failure mode for a typo should be the standard screen,
 * never a blank one. That fallback is also what retired `?layout=c` gracefully
 * — the link still resolves, and now to the layout it used to name.
 */
export function railFromSearch(search: string): ActiveRail {
  const value = new URLSearchParams(search).get("layout");
  if (value == null) return "none";

  const normalized = value.trim().toLowerCase();
  return (RAILS as readonly string[]).includes(normalized)
    ? (normalized as ActiveRail)
    : "none";
}
